// BottomNav.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { getoptionalAppAuthHeaders, isWebsiteLoggedIn } from '../utils/optionalAppAuth';
import { getGuestCartCount } from '../utils/guestCart';
import './BottomNav.css';

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [cartCount, setCartCount] = useState(0);

  const navItems = [
    { id: 'home', label: 'Home', icon: 'fas fa-home', path: '/' },
    { id: 'shop', label: 'Shop', icon: 'fas fa-store', path: '/shop' },
    { id: 'cart', label: 'Cart', icon: 'fas fa-shopping-cart', path: '/shop-cart' },
    { id: 'account', label: 'Account', icon: 'fas fa-user', path: '/accounts' },
  ];

  // Function to fetch cart count
  const fetchCartCount = useCallback(async () => {
    if (!isWebsiteLoggedIn()) {
      setCartCount(getGuestCartCount());
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/server/website/cart`, {
        headers: { ...getoptionalAppAuthHeaders() }
      });
      const json = await response.json();
      const cartItems = json?.data?.cart || [];
      const totalQty = cartItems.reduce((sum, item) => sum + Number(item?.quantity || 0), 0);
      setCartCount(totalQty);
    } catch (error) {
      console.error("Error fetching cart count:", error);
      setCartCount(0);
    }
  }, []);

  // Handle scroll to hide/show when reaching footer
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Check if user has scrolled near the footer (last 100px)
      const isNearFooter = currentScrollY + windowHeight >= documentHeight - 100;
      
      if (isNearFooter) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch cart count on mount
  useEffect(() => {
    fetchCartCount();
  }, [fetchCartCount]);

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      fetchCartCount();
    };
    
    window.addEventListener("cartWishlistUpdated", handleCartUpdate);
    window.addEventListener("profileUpdated", handleCartUpdate);
    
    return () => {
      window.removeEventListener("cartWishlistUpdated", handleCartUpdate);
      window.removeEventListener("profileUpdated", handleCartUpdate);
    };
  }, [fetchCartCount]);

  // Update when route changes (in case user logs in/out)
  useEffect(() => {
    fetchCartCount();
  }, [location.pathname, fetchCartCount]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className={`bottom-nav ${!isVisible ? 'bottom-nav--hidden' : ''}`}>
      {navItems.map((item) => (
        <button
          key={item.id}
          className={`bottom-nav__item ${isActive(item.path) ? 'bottom-nav__item--active' : ''}`}
          onClick={() => handleNavigation(item.path)}
        >
          <i className={item.icon}></i>
          <span>{item.label}</span>
          {/* Show cart badge only for cart item */}
          {item.id === 'cart' && cartCount > 0 && (
            <span className="bottom-nav__badge">{cartCount}</span>
          )}
        </button>
      ))}
    </div>
  );
};

export default BottomNav;